/**
 * Beatly Creator API (beatly-creator-api /cd) — calls outside api-v2.
 */

import { withRetry } from '@/utils/withRetry';

export interface CreatorApiError extends Error {
  status?: number;
  code?: string;
}

function getCreatorApiUrl(): string {
  const rawEnvValue = import.meta.env.VITE_CREATOR_API_URL;
  const apiUrl = typeof rawEnvValue === 'string' ? rawEnvValue.trim() : '';

  if (rawEnvValue !== undefined) {
    if (!apiUrl) {
      throw new Error('VITE_CREATOR_API_URL is set but empty');
    }
    return apiUrl;
  }
  if (import.meta.env.MODE === 'development') {
    const fallback = 'https://api-creator.staging.beatly.com';
    console.warn('[CreatorApi] VITE_CREATOR_API_URL not set, using fallback:', fallback);
    return fallback;
  }
  throw new Error('Creator API URL not configured. Set VITE_CREATOR_API_URL.');
}

function createApiError(message: string, status?: number, code?: string): CreatorApiError {
  const err = new Error(message) as CreatorApiError;
  err.status = status;
  err.code = code;
  return err;
}

function errorMessageFromBody(data: unknown, fallback: string): string {
  if (typeof data === 'object' && data !== null) {
    if ('message' in data && data.message != null) {
      return String((data as { message: unknown }).message);
    }
    if ('detail' in data && data.detail != null) {
      return String((data as { detail: unknown }).detail);
    }
  }
  return fallback;
}

export interface V1TeamMentionsSearchResponse {
  mentions: V1MentionItem[];
  page_size: number;
  page_number: number;
  has_more: boolean;
}

export interface V1MentionItem {
  mention_id: string;
  creatorlist_id?: string | null;
  keyword: string;
  /** All keywords matched on this post; optional because older backend
   * deploys only return the singular `keyword` field. */
  keywords?: string[];
  date_created: string;
  creator: {
    creator_id: string;
    name: string;
    handle: string;
    mugshot_url?: string | null;
    permalink?: string | null;
    followers_count?: number | null;
    engagement_rate?: number | null;
  };
  content: {
    content_id: string;
    creator_id: string;
    permalink?: string | null;
    preview_url?: string | null;
    video_url?: string | null;
    date_published: string;
    platform?: string | null;
    title?: string | null;
  };
}

/**
 * GET /cd/v1/mentions/search — latest mentions for the JWT team (dashboard).
 */
export async function getTeamMentionsSearch(
  token: string,
  pageSize = 5,
  pageNumber = 0
): Promise<V1TeamMentionsSearchResponse> {
  const base = getCreatorApiUrl().replace(/\/$/, '');
  const params = new URLSearchParams({
    page_size: String(pageSize),
    page_number: String(pageNumber),
  });
  const url = `${base}/cd/v1/mentions/search?${params.toString()}`;

  return withRetry(async () => {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    let data: unknown;
    try {
      const text = await response.text();
      data = text ? (JSON.parse(text) as unknown) : {};
    } catch {
      data = {};
    }

    if (!response.ok) {
      const message = errorMessageFromBody(data, `Request failed: ${response.status}`);
      const code =
        typeof data === 'object' && data !== null && 'code' in data
          ? String((data as { code: unknown }).code)
          : undefined;
      throw createApiError(message, response.status, code);
    }

    return data as V1TeamMentionsSearchResponse;
  });
}

/** Matches creator-api `BY_IDS_MAX_CREATOR_IDS`. */
const CREATOR_BY_IDS_MAX = 50;

function chunkIds<T>(arr: T[], size: number): T[][] {
  if (size <= 0) return [arr];
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

/**
 * GET /cd/v1/creator/:id — extended creator profile JSON (v1CreatorProfileExtended).
 */
export async function getCreatorExtendedProfile(creatorId: string, token: string): Promise<unknown> {
  const base = getCreatorApiUrl().replace(/\/$/, '');
  const url = `${base}/cd/v1/creator/${encodeURIComponent(creatorId)}`;
  return withRetry(async () => {
    const response = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    let data: unknown;
    try {
      const text = await response.text();
      data = text ? (JSON.parse(text) as unknown) : {};
    } catch {
      data = {};
    }
    if (!response.ok) {
      const message = errorMessageFromBody(data, `Request failed: ${response.status}`);
      const code =
        typeof data === 'object' && data !== null && 'code' in data
          ? String((data as { code: unknown }).code)
          : undefined;
      throw createApiError(message, response.status, code);
    }
    return data;
  });
}

/**
 * Batch GET /cd/v1/creators/by-ids (chunked). Returns map creator_id → JSON body for each row returned by the API.
 */
export async function getCreatorExtendedProfileBodiesById(
  creatorIds: string[],
  token: string
): Promise<Map<string, unknown>> {
  const byId = new Map<string, unknown>();
  if (creatorIds.length === 0) {
    return byId;
  }
  const base = getCreatorApiUrl().replace(/\/$/, '');
  const uniqueOrdered: string[] = [];
  const seen = new Set<string>();
  for (const id of creatorIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    uniqueOrdered.push(id);
  }

  for (const part of chunkIds(uniqueOrdered, CREATOR_BY_IDS_MAX)) {
    const params = new URLSearchParams();
    params.set('creator_ids', part.join(','));
    const url = `${base}/cd/v1/creators/by-ids?${params.toString()}`;
    await withRetry(async () => {
      const response = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      let data: unknown;
      try {
        const text = await response.text();
        data = text ? (JSON.parse(text) as unknown) : {};
      } catch {
        data = {};
      }
      if (!response.ok) {
        const message = errorMessageFromBody(data, `Request failed: ${response.status}`);
        const code =
          typeof data === 'object' && data !== null && 'code' in data
            ? String((data as { code: unknown }).code)
            : undefined;
        throw createApiError(message, response.status, code);
      }
      if (!Array.isArray(data)) {
        throw createApiError('Creator API by-ids: expected a JSON array', response.status, 'INVALID_RESPONSE');
      }
      for (const item of data) {
        if (!item || typeof item !== 'object') continue;
        const record = item as Record<string, unknown>;
        const cid = typeof record.creator_id === 'string' ? record.creator_id : null;
        if (!cid) continue;
        byId.set(cid, item);
      }
    });
  }

  return byId;
}

/** One content item from GET /cd/v1/content/search-sse (per-creator page). */
export interface V1CreatorContent {
  preview_url?: string | null;
  title?: string | null;
  date_published?: string;
  likes?: number | null;
  views?: number | null;
  comments?: number | null;
  permalink?: string | null;
  platform?: string | null;
  embed_url?: string | null;
}

/** One SSE JSON payload: one creator’s content page. */
export interface V1CreatorContentPage {
  creator_id: string;
  contents: V1CreatorContent[];
}

const SSE_CREATOR_BATCH_SIZE = 5;

/**
 * Stream contents for multiple creators (Server-Sent Events).
 * GET /cd/v1/content/search-sse — same batching as beatly-discovery-app.
 */
export function streamCreatorsContent(
  creatorIds: string[],
  token: string,
  onContentPage: (page: V1CreatorContentPage) => void,
  pageSize: number = 5
): { abort: () => void; promise: Promise<void> } {
  const abortController = new AbortController();

  const promise = (async () => {
    if (creatorIds.length === 0) {
      return;
    }

    if (!token) {
      throw createApiError('No creator authentication token available', 401, 'UNAUTHORIZED');
    }

    const apiUrl = getCreatorApiUrl().replace(/\/$/, '');

    const streamBatch = async (creatorIdBatch: string[]) => {
      if (abortController.signal.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      const creatorIdsParam = creatorIdBatch.join(',');
      const url = `${apiUrl}/cd/v1/content/search-sse?creator_ids=${encodeURIComponent(creatorIdsParam)}&page_size=${pageSize}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
        },
        signal: abortController.signal,
      });

      if (!response.ok) {
        let errorMessage = `Creator API request failed: ${response.status}`;
        let errorCode: string | undefined;
        try {
          const errorData = (await response.json()) as { message?: string; code?: string };
          errorMessage = errorData.message || errorMessage;
          errorCode = errorData.code;
        } catch {
          // use default message
        }
        throw createApiError(errorMessage, response.status, errorCode);
      }

      if (!response.body) {
        throw createApiError('Response body is null', 500, 'STREAM_ERROR');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop() || '';

          for (const event of events) {
            if (!event.trim()) continue;
            const lines = event.split('\n');
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const jsonStr = line.slice(6);
                try {
                  const contentPage = JSON.parse(jsonStr) as V1CreatorContentPage;
                  onContentPage(contentPage);
                } catch (parseError) {
                  console.warn('Failed to parse SSE event:', parseError, jsonStr);
                }
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    };

    for (let i = 0; i < creatorIds.length; i += SSE_CREATOR_BATCH_SIZE) {
      const batch = creatorIds.slice(i, i + SSE_CREATOR_BATCH_SIZE);
      await streamBatch(batch);
    }
  })();

  return {
    abort: () => abortController.abort(),
    promise,
  };
}

/**
 * Proxy URL for preview images (CORS / CDN). GET /cd/v1/content/proxy-image
 */
export function getImageProxyUrl(imageUrl: string | null | undefined, token: string | null | undefined): string | null {
  if (!imageUrl || imageUrl.trim() === '') {
    return null;
  }
  if (!token) {
    return null;
  }
  const apiUrl = getCreatorApiUrl().replace(/\/$/, '');
  const encodedUrl = encodeURIComponent(imageUrl);
  const encodedToken = encodeURIComponent(token);
  return `${apiUrl}/cd/v1/content/proxy-image?url=${encodedUrl}&token=${encodedToken}`;
}

/**
 * Prefer proxy URL when JWT is present; otherwise return the original URL for best-effort img src.
 */
export function getImageProxyUrlOrFallback(
  imageUrl: string | null | undefined,
  token: string | null | undefined
): string | null {
  const proxied = getImageProxyUrl(imageUrl, token);
  if (proxied) return proxied;
  if (!imageUrl || typeof imageUrl !== 'string' || !imageUrl.trim()) return null;
  return imageUrl.trim();
}
